import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiKey, FiChevronRight } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { getDashboardSummary, startScan, getScanConfigs } from "../../../api/credentialScanning";
import { showWarning } from "../../../utils/showMessage";
import FindingSummaryCards from "./components/FindingSummaryCards";
import SeverityBadge from "./components/SeverityBadge";
import OnboardingGuide from "./components/OnboardingGuide";
import CredScanBreadcrumb from "./components/CredScanBreadcrumb";
import "./index.scss";

/**
 * Landing page for Hardcoded Credentials Scanning.
 * Shows summary cards, last scan info, recent findings preview, and quick actions.
 */
const HardcodedCredentials = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [scanningConfig, setScanningConfig] = useState(null);
  const [scanMsg, setScanMsg] = useState("");

  const load = async () => {
    setLoading(true);
    const [summaryRes, configsRes] = await Promise.all([
      getDashboardSummary(),
      getScanConfigs(),
    ]);
    if (summaryRes.data) setSummary(summaryRes.data);
    if (configsRes.data) setConfigs(Array.isArray(configsRes.data) ? configsRes.data : []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleQuickScan = async (configId) => {
    setScanningConfig(configId);
    setScanMsg("");
    const { data, error } = await startScan(configId);
    if (error) {
      // Backend returns `{ warning: "..." }` when the scan can't proceed
      // because no targets are configured. Show that as a yellow warning
      // toast rather than the generic red error.
      if (error?.warning) {
        showWarning(error.warning);
        setScanMsg(error.warning);
      } else {
        setScanMsg(error?.error || t("credentialScanning.failedToStartScan"));
      }
    } else {
      setScanMsg(t("credentialScanning.scanStartedMessage", { scanId: data.scan_id }));
    }
    setScanningConfig(null);
  };

  return (
    <div>
      <CredScanBreadcrumb />
      <div style={{ padding: "16px 28px 24px" }}>
      {/* Title */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: 0, fontWeight: 700 }}>{t("credentialScanning.landingTitle")}</h4>
        <p style={{ margin: "6px 0 0", color: "#666", fontSize: 14 }}>
          {t("credentialScanning.landingDescription")}
        </p>
      </div>

      {/* Onboarding or Dashboard */}
      {!loading && (configs.length === 0 || !summary?.last_scan_at) ? (
        <OnboardingGuide
          gitRepoCount={summary?.git_repo_count ?? 0}
          configCount={configs.length}
          lastScanAt={summary?.last_scan_at}
          onNavigate={(path) => navigate(path)}
        />
      ) : (
      <>
      {/* Summary Cards */}
      <FindingSummaryCards summary={summary} loading={loading} />

      {/* Last Scan Info */}
      {summary && (
        <div className="cred-scan-landing-bar" style={{ borderRadius: 8, padding: "14px 18px", marginBottom: 24, display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <div>
            <span className="cred-scan-label" style={{ fontSize: 12 }}>{t("credentialScanning.lastScan")}</span>
            <div style={{ fontSize: 14, fontWeight: 500 }}>
              {summary.last_scan_at
                ? new Date(summary.last_scan_at).toLocaleString()
                : t("credentialScanning.never")}
            </div>
          </div>
          {summary.last_scan_status && (
            <div>
              <span className="cred-scan-label" style={{ fontSize: 12 }}>{t("credentialScanning.status")}</span>
              <div>
                <span
                  style={{
                    background:
                      summary.last_scan_status === "COMPLETED"
                        ? "#e8f5e9"
                        : summary.last_scan_status === "FAILED"
                        ? "#ffebee"
                        : "#e3f2fd",
                    color:
                      summary.last_scan_status === "COMPLETED"
                        ? "#2e7d32"
                        : summary.last_scan_status === "FAILED"
                        ? "#c62828"
                        : "#1565c0",
                    padding: "2px 10px",
                    borderRadius: 4,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  {summary.last_scan_status}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Scan message */}
      {scanMsg && (
        <div
          style={{
            background: "#e8f5e9",
            color: "#2e7d32",
            padding: "10px 14px",
            borderRadius: 6,
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          {scanMsg}
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 28 }}>
        <ActionCard
          title={t("credentialScanning.viewFindings")}
          description={t("credentialScanning.viewFindingsDescription")}
          color="#5177ff"
          onClick={() => navigate("findings")}
        />
        <ActionCard
          title={t("credentialScanning.scanConfigurations")}
          description={t("credentialScanning.scanConfigurationsDescription")}
          color="#43a047"
          onClick={() => navigate("configs")}
        />
        <ActionCard
          title={t("credentialScanning.scanHistory")}
          description={t("credentialScanning.scanHistoryDescription")}
          color="#f57c00"
          onClick={() => navigate("scan-history")}
        />
        <ActionCard
          title={t("credentialScanning.gitConnections")}
          description={t("credentialScanning.gitConnectionsDescription")}
          color="#6a1b9a"
          onClick={() => navigate("git-repos")}
        />
      </div>

      {/* Findings by type */}
      {summary?.by_type && Object.keys(summary.by_type).length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h6 style={{ fontWeight: 600, marginBottom: 12 }}>{t("credentialScanning.findingsByType")}</h6>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {Object.entries(summary.by_type)
              .sort((a, b) => b[1] - a[1])
              .map(([type, count]) => (
                <button
                  key={type}
                  onClick={() => navigate("findings", { state: { credentialType: type } })}
                  style={{
                    background: "#f0f4ff",
                    border: "1px solid #c5d0ff",
                    borderRadius: 6,
                    padding: "6px 12px",
                    fontSize: 12,
                    cursor: "pointer",
                    transition: "background 0.15s, border-color 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#dce6ff";
                    e.currentTarget.style.borderColor = "#5177ff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#f0f4ff";
                    e.currentTarget.style.borderColor = "#c5d0ff";
                  }}
                  title={t("credentialScanning.viewAllTypeFindings", { type: type.replace(/_/g, " ") })}
                >
                  <span style={{ fontWeight: 600, color: "#5177ff" }}>{count}</span>
                  <span style={{ marginLeft: 6, color: "#555" }}>
                    {type.replace(/_/g, " ")}
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Scan Configs Quick Start */}
      {configs.length > 0 && (
        <div>
          <h6 style={{ fontWeight: 600, marginBottom: 12 }}>{t("credentialScanning.quickScan")}</h6>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {configs.slice(0, 4).map((config) => (
              <button
                key={config.id}
                onClick={() => handleQuickScan(config.id)}
                disabled={scanningConfig === config.id}
                style={{
                  padding: "8px 16px",
                  background: scanningConfig === config.id ? "#aaa" : "#fff",
                  border: "2px solid #5177ff",
                  borderRadius: 8,
                  color: "#5177ff",
                  fontWeight: 600,
                  cursor: scanningConfig === config.id ? "not-allowed" : "pointer",
                  fontSize: 13,
                }}
              >
                {scanningConfig === config.id ? t("credentialScanning.starting") : `▶ ${config.name}`}
              </button>
            ))}
          </div>
        </div>
      )}

      {configs.length === 0 && !loading && (
        <div className="cred-scan-empty-state" style={{ textAlign: "center", padding: "40px 20px", borderRadius: 10, border: "2px dashed #ddd" }}>
          <p style={{ fontSize: 16, margin: "0 0 12px" }}>
            {t("credentialScanning.noScanConfigurations")}
          </p>
          <button
            onClick={() => navigate("configs")}
            style={{
              padding: "8px 20px",
              background: "#5177ff",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontWeight: 600,
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            {t("credentialScanning.createFirstConfiguration")}
          </button>
        </div>
      )}

      {/* Latest Findings */}
      <LatestFindings t={t} findings={summary?.latest_findings || []} onViewAll={() => navigate("findings")} />

      </>
      )}
      </div>
    </div>
  );
};

const ActionCard = ({ title, description, color, onClick }) => (
  <button
    onClick={onClick}
    style={{
      padding: "14px 20px",
      background: "#fff",
      border: `2px solid ${color}`,
      borderRadius: 8,
      cursor: "pointer",
      textAlign: "left",
      minWidth: 180,
      transition: "background 0.15s",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = color + "0f";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "#fff";
    }}
  >
    <div style={{ fontWeight: 700, color, fontSize: 14, marginBottom: 4 }}>
      {title}
    </div>
    <div style={{ fontSize: 12, color: "#666" }}>{description}</div>
  </button>
);

const LatestFindings = ({ t, findings, onViewAll }) => {
  if (!findings || findings.length === 0) return null;

  const columnLabels = [
    t("credentialScanning.colSecretType"),
    t("credentialScanning.colSource"),
    t("credentialScanning.colSeverity"),
    t("credentialScanning.colStatus"),
    t("credentialScanning.colDetected"),
  ];

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h6 style={{ fontWeight: 600, margin: 0 }}>{t("credentialScanning.latestFindings")}</h6>
        <button
          onClick={onViewAll}
          style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            background: "none", border: "none", color: "#5177ff",
            fontWeight: 600, fontSize: 13, cursor: "pointer",
          }}
        >
          {t("credentialScanning.viewAll")} <FiChevronRight size={12} />
        </button>
      </div>
      <div
        style={{
          background: "var(--card-bg, #fff)",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {columnLabels.map((h) => (
                <th
                  key={h}
                  style={{
                    fontSize: 11, fontWeight: 600, color: "#717171",
                    textTransform: "uppercase", letterSpacing: "0.5px",
                    padding: "10px 14px", borderBottom: "1px solid #eee",
                    textAlign: "left", background: "var(--card-bg, #fafafa)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {findings.map((f) => (
              <tr key={f.id}>
                <td style={{ padding: "12px 14px", fontSize: 13, borderBottom: "1px solid #f0f0f0" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <FiKey size={13} style={{ color: "#999" }} />
                    <span style={{ fontWeight: 500 }}>{(f.credential_type || "").replace(/_/g, " ")}</span>
                  </span>
                </td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "#666", borderBottom: "1px solid #f0f0f0" }}>
                  <SourceLabel t={t} finding={f} />
                </td>
                <td style={{ padding: "12px 14px", borderBottom: "1px solid #f0f0f0" }}>
                  <SeverityBadge severity={f.severity} />
                </td>
                <td style={{ padding: "12px 14px", borderBottom: "1px solid #f0f0f0" }}>
                  <span className={`cred-scan-status-badge ${f.status}`}>{formatStatus(f.status, t)}</span>
                </td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "#666", borderBottom: "1px solid #f0f0f0" }}>
                  {f.first_seen ? new Date(f.first_seen).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SOURCE_LABEL_KEYS = {
  ssh_asset: "credentialScanning.sourceSshServer",
  rdp_asset: "credentialScanning.sourceWindowsServer",
  database: "credentialScanning.sourceDatabase",
  git_repo: "credentialScanning.sourceGitRepository",
};

const SourceLabel = ({ t, finding }) => {
  const type = finding.source_type || "";
  const labelKey = SOURCE_LABEL_KEYS[type];
  const label = labelKey ? t(labelKey) : type.replace(/_/g, " ");

  if (type === "git_repo") {
    return <span>{finding.repository_name || label}</span>;
  }

  const detail = finding.asset_name || "";
  return (
    <span>
      {label}
      {detail && <span style={{ color: "#999", marginLeft: 4 }}>({detail})</span>}
    </span>
  );
};

const formatStatus = (status, t) => {
  switch (status) {
    case "OPEN": return t("credentialScanning.statusOpen");
    case "ACKNOWLEDGED": return t("credentialScanning.statusAcknowledged");
    case "REMEDIATED": return t("credentialScanning.statusRemediated");
    case "FALSE_POSITIVE": return t("credentialScanning.statusFalsePositive");
    default: return status;
  }
};

export default HardcodedCredentials;
